import { createContext, useContext, useState, useRef, useCallback, type ReactNode } from 'react';

interface DataAgentBridgeContextType {
  pendingMessage: string | null;
  openAgentWithMessage: (message: string) => void;
  consumePendingMessage: () => string | null;
  excludeInternal: boolean;
  setExcludeInternal: (value: boolean) => void;
}

const DataAgentBridgeContext = createContext<DataAgentBridgeContextType | null>(null);

interface DataAgentBridgeProviderProps {
  onOpenAgent: () => void;
  children: ReactNode;
}

export function DataAgentBridgeProvider({ onOpenAgent, children }: DataAgentBridgeProviderProps) {
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const [excludeInternal, setExcludeInternal] = useState(true);

  const openAgentWithMessage = useCallback((message: string) => {
    pendingRef.current = message;
    setPendingMessage(message);
    onOpenAgent();
  }, [onOpenAgent]);

  const consumePendingMessage = useCallback(() => {
    const msg = pendingRef.current;
    pendingRef.current = null;
    setPendingMessage(null);
    return msg;
  }, []);

  return (
    <DataAgentBridgeContext.Provider
      value={{
        pendingMessage,
        openAgentWithMessage,
        consumePendingMessage,
        excludeInternal,
        setExcludeInternal,
      }}
    >
      {children}
    </DataAgentBridgeContext.Provider>
  );
}

export function useDataAgentBridge() {
  const ctx = useContext(DataAgentBridgeContext);
  if (!ctx) {
    throw new Error('useDataAgentBridge must be used within a DataAgentBridgeProvider');
  }
  return ctx;
}
